/* V5.1: restore REAL mobile GLB phone entry; shared uploads; desktop implementation unchanged. */
let THREE, GLTFLoader, DRACOLoader;
let desktopLibraries;
function loadDesktopLibraries() {
  if (!desktopLibraries) desktopLibraries = Promise.all([
    import('./three.module.js'), import('./GLTFLoader.js'), import('./DRACOLoader.js')
  ]).then(([three, gltf, draco]) => {
    THREE = three; GLTFLoader = gltf.GLTFLoader; DRACOLoader = draco.DRACOLoader;
  }).catch(error => { desktopLibraries = null; throw error; });
  return desktopLibraries;
}


class DesktopPhoneTunnel {
  constructor(host) { this.host = host; }
  get dataset() { return this.host.dataset; }
  get style() { return this.host.style; }
  get offsetHeight() { return this.host.offsetHeight; }
  get clientWidth() { return this.host.clientWidth; }
  get clientHeight() { return this.host.clientHeight; }
  querySelector(selector) { return this.host.querySelector(selector); }
  querySelectorAll(selector) { return this.host.querySelectorAll(selector); }
  addEventListener(...args) { return this.host.addEventListener(...args); }
  getBoundingClientRect() { return this.host.getBoundingClientRect(); }

  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.dead = false;
    this.abort = new AbortController();
    this.resources = new Set();
    this.videos = []; // legacy video fallback entries
    this.animatedCards = [];
    this.coversReady = false;
    this.coverLoadPromises = [];
    this.anim = [];
    this.cardGroups = [];
    this.clickTargets = [];
    this.mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    this.pointer = null;
    this.hovered = null;
    this.tunnelProgress = 0;
    this.tunnelTarget = 0;
    this.visible = true;
    this.last = performance.now();
    this.scrollRaf = 0;
    this.frame = 0;
    this.lastWebpLayoutAt = 0;
    this.lastAnimatedSelectionAt = 0;
    this.isScrolling = false;
    this.scrollIdleTimer = 0;
    this.lastSceneRenderAt = 0;
    this.allowAnimatedWebp = false;
    this.init().catch(error => this.fail(error));
  }

  own(resource) { if (resource) this.resources.add(resource); return resource; }
  clamp(v, a = 0, b = 1) { return Math.min(b, Math.max(a, v)); }
  lerp(a, b, t) { return a + (b - a) * t; }
  ease(t) { return t < .5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }
  range(p, a, b) { return this.clamp((p - a) / Math.max(.0001, b - a)); }

  log(message) {
    console.info('[VOGALAB PhoneTunnel]', message);
    if (this.debug3d) this.debug3d.textContent += (this.debug3d.textContent ? '\n' : '') + message;
  }

  async init() {
    this.config = JSON.parse(this.querySelector('[data-vpt-config]').textContent);
    this.cards = this.config.cards || [];
    this.sticky = this.querySelector('[data-vpt-sticky]');
    this.phone = this.querySelector('[data-vpt-phone]');
    this.screen = this.querySelector('[data-vpt-screen]');
    this.stage = this.querySelector('[data-vpt-stage]');
    this.status = this.querySelector('[data-vpt-status]');
    this.fallback = this.querySelector('[data-vpt-fallback]');
    this.debug3d = this.querySelector('[data-vpt-3d-debug]');
    this.hint = this.querySelector('[data-vpt-hint]');
    this.intro = this.querySelector('[data-vpt-intro]');
    this.cardCopyLayer = this.querySelector('[data-vpt-card-copy-layer]');
    this.cardCopyEls = Array.from(this.querySelectorAll('[data-vpt-card-copy]'));
    this.screenClip = this.querySelector('.vpt-screen__clip');
    this.webpLayer = this.querySelector('[data-vpt-webp-layer]');
    if (!this.webpLayer && this.screenClip) {
      this.webpLayer = document.createElement('div');
      this.webpLayer.className = 'vpt-webp-layer';
      this.webpLayer.setAttribute('data-vpt-webp-layer', '');
      this.webpLayer.setAttribute('aria-hidden', 'true');
      this.screenClip.appendChild(this.webpLayer);
    }
    this.progressBar = this.querySelector('[data-vpt-tunnel-progress]');
    this.tunnelLabel = this.querySelector('[data-vpt-tunnel-label]');
    this.scrollDebug = this.querySelector('[data-vpt-scroll-debug]');
    this.phaseDebug = this.querySelector('[data-vpt-phase-debug]');
    this.scaleDebug = this.querySelector('[data-vpt-scale-debug]');
    this.tunnelDebug = this.querySelector('[data-vpt-tunnel-debug]');
    this.mobileMq = matchMedia('(max-width: 749px)');
    this.reduceMotionPreferred = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.reduced = false; // Never jump the storytelling section to its final state automatically.

    if (!this.cards.length) throw new Error('请在主题编辑器添加至少一张 3D 媒体卡片。');

    // 先画出手机裁切区域，再启动 WebGL。GLB/图片继续在后台渐进加载，
    // 不再等所有 Card 图片加载完才出现第一帧。
    this.updateScroll();
    this.setupRenderer();
    this.setupLoaders();
    this.resize(true);
    this.bindEvents();
    this.resetToStartIfNeeded();

    this.resizeObserver = new ResizeObserver(() => this.resize(true));
    this.resizeObserver.observe(this.sticky);

    // JS 已正常启动。即使模型还在加载，也不要触发 Theme Editor 的“脚本未初始化”假报警。
    this.dataset.ready = 'true';
    this.last = performance.now();
    this.frame = requestAnimationFrame(t => this.tick(t));

    await this.buildScene();
    if (this.dead) return;
    this.log('3D 场景初始化完成 · V4.4 WebP连续播放与穿帮修复');
  }

  setupRenderer() {
    // Stable renderer: one viewport-sized drawing buffer for the entire sequence.
    // The phone screen is rendered with WebGL scissor/viewport instead of resizing
    // the canvas from ~170px wide to several thousand pixels while scrolling.
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      premultipliedAlpha: false,
      powerPreference: 'high-performance'
    });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio || 1, this.mobileMq.matches ? 1.0 : 1.25));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.22;
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.autoClear = false;
    this.stage.prepend(this.renderer.domElement);
    this.renderer.domElement.addEventListener('webglcontextlost', event => {
      event.preventDefault();
      this.log('WebGL Context Lost：浏览器/GPU 已暂停 3D。稳定版不会主动反复重建 framebuffer。');
      if (this.status) {
        this.status.style.display = 'block';
        this.status.textContent = '3D 图形上下文被浏览器暂停，请刷新页面后重试。';
      }
    }, { signal: this.abort.signal });
    this.maxAniso = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(this.config.worldColor || '#f7f1e6');
    this.scene.fog = new THREE.FogExp2(this.config.worldColor || '#f7f1e6', .009);
    this.camera = new THREE.PerspectiveCamera(this.config.fov || 48, 1, .08, 150);
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0xd8c8b0, 3.4));
    const sun = new THREE.DirectionalLight(0xfff6ea, 3.0);
    sun.position.set(-3, 7, 5);
    this.scene.add(sun);

    this.screenClip?.classList.toggle('has-postfx', !!this.config.postFx);
  }

  setupLoaders() {
    this.draco = new DRACOLoader();
    this.draco.setDecoderConfig({ type: 'js' });
    const decoder = new URL(this.config.decoder, location.href);
    this.draco.setDecoderPath(new URL('./', decoder).href);
    this.loader = new GLTFLoader().setDRACOLoader(this.draco);
  }

  async buildScene() {
    const sceneSpacing = Number(this.mobileMq.matches ? this.config.cardSpacingMobile : this.config.cardSpacing) || (this.mobileMq.matches ? 12 : 8);
    this.depth = Math.max(40, this.cards.length * sceneSpacing + 16);
    const useVertical = false; // Shared landscape photo window on both layouts.
    const frameURL = useVertical && this.config.vertical ? this.config.vertical : this.config.horizontal;

    // 两个模型并行请求，但不等待 Card Frame 才显示 Tunnel。
    const tunnelPromise = this.safeLoad(this.config.tunnel, 'Tunnel GLB');
    const framePromise = this.safeLoad(frameURL, useVertical ? 'Vertical card GLB' : 'Horizontal card GLB');

    const tunnel = await tunnelPromise;
    if (this.dead) return;

    if (tunnel) this.mountTunnel(tunnel);
    else { this.basicTunnel(); this.log('Tunnel: 使用内置程序化隧道'); }

    // Tunnel 一出现就立刻有灯光/墙面，不再等所有卡片媒体下载。
    this.lights();
    this.setupFinish();
    this.cardDecor();
    if (this.config.playfulProps) this.toys();
    this.tunnelReady = true;
    if (this.status) {
      this.status.textContent = '';
      this.status.style.display = 'none';
    }

    const frame = await framePromise;
    if (this.dead) return;
    if (!frame) this.log('Card frame: 使用内置程序化卡框');

    // V4.0：先创建全部卡片并只加载静态封面。
    // 所有封面完成（成功或失败）之后，才允许请求动画 WebP / 备用视频，
    // 避免首屏图片与动画同时抢带宽。
    const coverJobs = [];
    for (let i = 0; i < this.cards.length; i++) {
      coverJobs.push(this.addCard(this.cards[i], i, frame, useVertical));
    }
    // 墙面图同样属于首轮静态资源。只有卡片封面 + 墙面图都完成后，
    // 才允许动画 WebP 开始请求，保证“静态图片优先，动画后加载”。
    const staticDecorJobs = (this.config.decor || []).map(url => this.preloadStaticImage(url));
    await Promise.allSettled([...coverJobs, ...staticDecorJobs]);
    if (this.dead) return;
    this.coversReady = true;
    this.log('静态图片阶段完成（卡片封面 + 墙面图）→ 动画 WebP 已允许按需加载（同时最多 2 个播放）');
  }

  preloadStaticImage(url) {
    if (!url) return Promise.resolve();
    return new Promise(resolve => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = url;
      if (img.complete && img.naturalWidth > 0) resolve();
    });
  }

  track(root) {
    root.traverse(node => {
      if (!node.isMesh) return;
      this.own(node.geometry);
      const mats = Array.isArray(node.material) ? node.material : [node.material];
      for (const material of mats) {
        this.own(material);
        if (!material) continue;
        for (const value of Object.values(material)) if (value?.isTexture) this.own(value);
      }
    });
  }

  async safeLoad(url, label) {
    if (!url) { this.log(`${label}: URL 为空`); return null; }
    try {
      this.log(`${label}: 开始加载`);
      const result = await this.loader.loadAsync(url);
      if (this.dead) {
        result.scene.traverse(n => { n.geometry?.dispose?.(); const ms = Array.isArray(n.material) ? n.material : [n.material]; ms.forEach(m => { if (!m) return; Object.values(m).forEach(v => { if (v?.isTexture) v.dispose(); }); m.dispose?.(); }); });
        return null;
      }
      this.track(result.scene);
      this.log(`${label}: ✓ 加载成功`);
      return result.scene;
    } catch (error) {
      console.warn('[VOGALAB PhoneTunnel]', label, error);
      this.log(`${label}: ✗ 加载失败，使用备用模型`);
      return null;
    }
  }

  mountTunnel(tunnel) {
    tunnel.rotation.y = Math.PI / 2;
    tunnel.updateMatrixWorld(true);
    const bounds = new THREE.Box3().setFromObject(tunnel);
    const size = bounds.getSize(new THREE.Vector3());
    const center = bounds.getCenter(new THREE.Vector3());
    const container = new THREE.Group();
    tunnel.position.sub(center);
    container.add(tunnel);
    container.scale.set(12 / Math.max(size.x, .01), 7 / Math.max(size.y, .01), this.depth / Math.max(size.z, .01));
    container.position.set(0, 3.5, -this.depth / 2 + 3);
    const wallMaterial = this.own(new THREE.MeshStandardMaterial({ color: 0xd6b385, roughness: .85, metalness: 0, side: THREE.DoubleSide, flatShading: true }));
    tunnel.traverse(node => {
      if (!node.isMesh) return;
      node.material = wallMaterial;
      this.tunnelWalls = node;
    });
    this.scene.add(container);
  }

  box(w, h, d, x, y, z, material) {
    const mesh = new THREE.Mesh(this.own(new THREE.BoxGeometry(w, h, d)), material);
    mesh.position.set(x, y, z);
    this.scene.add(mesh);
    return mesh;
  }

  basicTunnel() {
    const m = this.own(new THREE.MeshStandardMaterial({ color: 0xd6b385, roughness: .85, metalness: 0 }));
    this.box(12, .15, this.depth, 0, -.08, -this.depth / 2, m);
    this.box(.2, 7, this.depth, -6, 3.5, -this.depth / 2, m);
    this.box(.2, 7, this.depth, 6, 3.5, -this.depth / 2, m);
    this.box(12, .2, this.depth, 0, 7, -this.depth / 2, m);
  }

  lights() {
    const rail = this.own(new THREE.MeshBasicMaterial({ color: 0xfff8ec, toneMapped: false }));
    const glowCanvas = document.createElement('canvas');
    glowCanvas.width = 128; glowCanvas.height = 4;
    const ctx = glowCanvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 128, 0);
    gradient.addColorStop(0, 'rgba(240,230,210,0)');
    gradient.addColorStop(.5, 'rgba(240,230,210,.50)');
    gradient.addColorStop(1, 'rgba(240,230,210,0)');
    ctx.fillStyle = gradient; ctx.fillRect(0, 0, 128, 4);
    const glowTexture = this.own(new THREE.CanvasTexture(glowCanvas));
    const glowMaterial = this.own(new THREE.MeshBasicMaterial({ map: glowTexture, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, toneMapped: false }));
    for (const x of [-4.3, 4.3]) {
      this.box(.045, .035, this.depth, x, .10, -this.depth / 2, rail);
      const glow = new THREE.Mesh(this.own(new THREE.PlaneGeometry(1.25, this.depth)), glowMaterial);
      glow.rotation.x = -Math.PI / 2; glow.position.set(x, .095, -this.depth / 2); this.scene.add(glow);
    }
    const white = this.own(new THREE.MeshBasicMaterial({ color: 0xfffdf8, toneMapped: false }));
    for (let z = -1; z > -this.depth; z -= 6) this.box(.20, .045, .8, 0, 5.95, z, white);
  }

  setupFinish() {
    // No off-screen render target in the stable build.
    // The previous version rendered to a WebGLRenderTarget and resized that target
    // continuously with the animated phone screen. That caused GPU reallocations and
    // visible black flashes in Shopify's theme-editor iframe.
    // A lightweight CSS vignette is used instead (see phone-tunnel.css).
  }

  kraft() {
    const c = document.createElement('canvas'); c.width = 1024; c.height = 1024;
    const x = c.getContext('2d'); x.fillStyle = '#c9a26f'; x.fillRect(0, 0, 1024, 1024);
    for (let i = 0; i < 2600; i++) { x.fillStyle = `rgba(100,70,35,${Math.random() * .06})`; x.fillRect(Math.random() * 1024, Math.random() * 1024, 3, 3); }
    x.strokeStyle = 'rgba(120,80,40,.15)'; x.lineWidth = 3;
    for (let i = 0; i < 1024; i += 46) { x.beginPath(); x.moveTo(i, 0); x.lineTo(i, 1024); x.stroke(); }
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.wrapS = t.wrapT = THREE.RepeatWrapping; t.anisotropy = this.maxAniso; return this.own(t);
  }

  cardDecor() {
    const wall = this.kraft(); wall.repeat.set(8, 2);
    if (this.tunnelWalls) { this.tunnelWalls.material.map = wall; this.tunnelWalls.material.color.set(0xffffff); this.tunnelWalls.material.needsUpdate = true; }
    this.brandPanels();

    const roadC = document.createElement('canvas'); roadC.width = 256; roadC.height = 1024;
    const rx = roadC.getContext('2d'); rx.fillStyle = '#cbb185'; rx.fillRect(0, 0, 256, 1024); rx.fillStyle = '#2b2b2b';
    for (let y = 40; y < 1024; y += 128) rx.fillRect(118, y, 20, 64);
    const roadT = this.own(new THREE.CanvasTexture(roadC)); roadT.colorSpace = THREE.SRGBColorSpace; roadT.wrapS = roadT.wrapT = THREE.RepeatWrapping; roadT.repeat.set(1, this.depth / 14); roadT.anisotropy = this.maxAniso;
    const road = new THREE.Mesh(this.own(new THREE.PlaneGeometry(2.1, this.depth)), this.own(new THREE.MeshStandardMaterial({ map: roadT, roughness: .9 })));
    road.rotation.x = -Math.PI / 2; road.position.set(0, .155, -this.depth / 2 + 3); this.scene.add(road);
    const curb = this.own(new THREE.MeshStandardMaterial({ color: 0x2b2b2b, roughness: .6 }));
    for (const s of [-1, 1]) { const edge = new THREE.Mesh(this.own(new THREE.BoxGeometry(.12, .06, this.depth)), curb); edge.position.set(s * 1.12, .16, -this.depth / 2 + 3); this.scene.add(edge); }
  }

  brandPanels() {
    const decor = this.config.decor || []; if (!decor.length) return;
    const frameMat = this.own(new THREE.MeshStandardMaterial({ color: 0x1c1c1c, roughness: .5 }));
    const load = url => new THREE.TextureLoader().loadAsync(url).then(t => { this.own(t); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = this.maxAniso; return t; }).catch(() => null);
    for (let i = 0; i < Math.floor(this.depth / 9); i++) {
      const side = i % 2 ? 1 : -1, z = -8 - i * 9, url = decor[i % decor.length];
      const frame = new THREE.Mesh(this.own(new THREE.BoxGeometry(2.62, 1.78, .05)), frameMat); frame.position.set(side * 5.72, 3.3, z); frame.rotation.y = -side * Math.PI / 2;
      const panel = new THREE.Mesh(this.own(new THREE.PlaneGeometry(2.44, 1.6)), this.own(new THREE.MeshBasicMaterial({ color: 0xefe7db, toneMapped: false })));
      panel.position.set(side * 5.68, 3.3, z); panel.rotation.y = -side * Math.PI / 2;
      this.scene.add(frame, panel);
      load(url).then(t => { if (this.dead || !t) return; panel.material.map = t; panel.material.color.set(0xffffff); panel.material.needsUpdate = true; });
    }
    const banner = new THREE.Mesh(this.own(new THREE.PlaneGeometry(4.6, 1.05)), this.own(new THREE.MeshBasicMaterial({ color: 0xefe7db, toneMapped: false, transparent: true })));
    banner.position.set(0, 5.4, -3.2); this.scene.add(banner);
    load(decor[decor.length - 1]).then(t => { if (this.dead || !t) return; banner.material.map = t; banner.material.color.set(0xffffff); banner.material.needsUpdate = true; });
  }

  toys() {
    const colors = [0xff6b6b, 0x4ecdc4, 0xffd93d, 0x6a8dff, 0xff9a3d, 0xb28dff];
    const star = new THREE.Shape();
    for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + i * Math.PI / 5, r = i % 2 ? .16 : .36; star[i ? 'lineTo' : 'moveTo'](Math.cos(a) * r, Math.sin(a) * r); }
    star.closePath();
    const starGeo = this.own(new THREE.ShapeGeometry(star));
    for (let i = 0; i < 18; i++) {
      const m = this.own(new THREE.MeshStandardMaterial({ color: colors[i % colors.length], roughness: .4, side: THREE.DoubleSide }));
      const s = new THREE.Mesh(starGeo, m); const side = i % 2 ? 1 : -1;
      s.position.set(side * (3.7 + Math.random() * 1.4), 1.4 + Math.random() * 3.8, -6 - i * 4.8 - Math.random() * 2); s.rotation.z = Math.random() * 6; s.scale.setScalar(.7 + Math.random() * .75); this.scene.add(s);
      const speed = .35 + Math.random() * .7; this.anim.push(t => { s.rotation.z = t * speed * (side > 0 ? 1 : -1); });
    }
  }

  async addCard(data, i, frame, narrow) {
    const spacing = Number(this.mobileMq.matches ? this.config.cardSpacingMobile : this.config.cardSpacing) || (this.mobileMq.matches ? 12 : 8);
    const group = new THREE.Group();
    const baseX = narrow ? (i % 2 === 0 ? -.55 : .55) : (i % 2 === 0 ? -1.35 : 1.45);
    group.position.set(baseX, 3.25, -10 - i * spacing);
    group.rotation.y = i % 2 === 0 ? .10 : -.10;
    group.userData = { baseX, side: i % 2 === 0 ? -1 : 1, clearance: narrow ? 1.1 : 1.5, hover: 0, hoverT: 0, pop: 0 };

    const width = 4.6;
    const mediaAspect = Math.max(.5, Number(this.config.mediaAspect) || 16 / 9);
    const height = width * .90 / (.86 * mediaAspect);
    if (frame) {
      const copy = frame.clone(true); copy.rotation.y += Math.PI / 2; copy.updateMatrixWorld(true);
      const bounds = new THREE.Box3().setFromObject(copy), size = bounds.getSize(new THREE.Vector3()), center = bounds.getCenter(new THREE.Vector3()); copy.position.sub(center);
      const wrap = new THREE.Group(); wrap.add(copy); wrap.scale.set(width / Math.max(size.x, .01), height / Math.max(size.y, .01), .13 / Math.max(size.z, .01));
      const toyMat = this.own(new THREE.MeshStandardMaterial({ color: 0x1c1c1c, roughness: .4, metalness: .1 }));
      copy.traverse(node => {
        if (!node.isMesh) return;
        const names = (Array.isArray(node.material) ? node.material : [node.material]).map(m => m?.name || '');
        if (names.some(name => name.startsWith('card_front'))) node.visible = false;
        else node.material = toyMat;
      });
      group.add(wrap);
    } else {
      group.add(new THREE.Mesh(this.own(new THREE.BoxGeometry(width, height, .12)), this.own(new THREE.MeshStandardMaterial({ color: 0x1c1c1c, metalness: .1, roughness: .4 }))));
    }

    // Layer 1: static cover. It is always kept underneath the animated layer,
    // so switching between cover and animation can never flash to blank/white.
    const coverSurface = new THREE.Mesh(
      this.own(new THREE.PlaneGeometry(width * .90, height * .86)),
      this.own(new THREE.MeshBasicMaterial({ color: 0xefe7db, toneMapped: false }))
    );
    coverSurface.position.z = .090;
    coverSurface.userData.link = data.link;
    group.add(coverSurface);
    this.clickTargets.push(coverSurface);

    // Layer 2: animated WebP/video. It starts transparent and only fades in
    // AFTER its first real frame is ready. Cover remains visible below it.
    const animatedSurface = new THREE.Mesh(
      coverSurface.geometry,
      this.own(new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false, toneMapped: false }))
    );
    animatedSurface.position.z = .095;
    animatedSurface.visible = false;
    animatedSurface.userData.link = data.link;
    group.add(animatedSurface);
    this.clickTargets.push(animatedSurface);

    const coverURL = data.image;
    let coverPromise = Promise.resolve();
    if (coverURL) {
      coverPromise = new THREE.TextureLoader().loadAsync(coverURL).then(tex => {
        if (this.dead) { tex.dispose?.(); return; }
        this.own(tex);
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.anisotropy = this.maxAniso;
        this.cover(tex, mediaAspect);
        coverSurface.material.map = tex;
        coverSurface.material.color.set(0xffffff);
        coverSurface.material.needsUpdate = true;
      }).catch(error => console.warn('[VOGALAB PhoneTunnel] Cover unavailable', coverURL, error));
    }

    const animatedWebpURL = data.animatedWebp;
    const fallbackVideoURL = data.video || '';
    if (animatedWebpURL || fallbackVideoURL) {
      let domImage = null;
      if (animatedWebpURL && this.webpLayer) {
        domImage = document.createElement('img');
        domImage.className = 'vpt-animated-webp';
        domImage.alt = '';
        domImage.draggable = false;
        domImage.decoding = 'async';
        domImage.loading = 'eager';
        domImage.style.transformOrigin = '0 0';
        domImage.setAttribute('aria-hidden', 'true');
        this.webpLayer.appendChild(domImage);
      }
      this.animatedCards.push({
        index: i,
        group,
        surface: animatedSurface,
        type: animatedWebpURL ? 'webp' : 'video',
        url: animatedWebpURL || fallbackVideoURL,
        aspect: mediaAspect,
        loading: false,
        loaded: false,
        active: false,
        fade: 0,
        targetFade: 0,
        lastActiveAt: 0,
        inactiveSince: 0,
        domImage,
        domSrc: '',
        domBaseW: 1000,
        domBaseH: Math.max(1, Math.round(1000 / mediaAspect)),
        lastDomTransform: '',
        texture: null,
        video: null,
        projection: null
      });
    }

    group.userData.cardWidth = width;
    group.userData.cardHeight = height;
    this.scene.add(group);
    this.cardGroups.push(group);

    // Returning this promise makes buildScene wait for ALL static covers before
    // any animated WebP is allowed to start requesting/decoding.
    return coverPromise;
  }

  drawCoverFrame(source, canvas, ctx) {
    const sw = source.videoWidth || source.naturalWidth || source.width || 1;
    const sh = source.videoHeight || source.naturalHeight || source.height || 1;
    const dw = canvas.width, dh = canvas.height;
    const sourceAspect = sw / sh;
    const targetAspect = dw / dh;
    let sx = 0, sy = 0, sWidth = sw, sHeight = sh;
    if (sourceAspect > targetAspect) {
      sWidth = sh * targetAspect;
      sx = (sw - sWidth) * .5;
    } else {
      sHeight = sw / targetAspect;
      sy = (sh - sHeight) * .5;
    }
    ctx.clearRect(0, 0, dw, dh);
    ctx.drawImage(source, sx, sy, sWidth, sHeight, 0, 0, dw, dh);
  }

  async ensureAnimatedLoaded(item) {
    if (!this.coversReady || item.loaded || item.loading || this.dead) return;
    item.loading = true;
    try {
      if (item.type === 'webp') {
        // IMPORTANT: animated WebP cannot be animated by drawing an HTMLImageElement
        // into Canvas/WebGL. Canvas drawImage() intentionally uses the poster/first frame.
        // Keep the WebP as a native DOM <img>; the browser then advances its animation.
        const image = item.domImage;
        if (!image) throw new Error('Native WebP layer unavailable');
        await new Promise((resolve, reject) => {
          const done = () => { cleanup(); resolve(); };
          const fail = () => { cleanup(); reject(new Error('Animated WebP failed to load')); };
          const cleanup = () => {
            image.removeEventListener('load', done);
            image.removeEventListener('error', fail);
          };
          image.addEventListener('load', done, { once: true });
          image.addEventListener('error', fail, { once: true });
          item.domSrc = item.url;
          image.src = item.url;
          if (image.complete && image.naturalWidth > 0) done();
        });
        if (this.dead) return;
        item.loaded = true;
      } else {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.src = item.url;
        await new Promise((resolve, reject) => {
          video.addEventListener('loadeddata', resolve, { once: true });
          video.addEventListener('error', reject, { once: true });
          video.load();
        });
        if (this.dead) return;
        const texture = this.own(new THREE.VideoTexture(video));
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.anisotropy = this.maxAniso;
        this.cover(texture, item.aspect);
        item.video = video;
        item.texture = texture;
        item.surface.material.map = texture;
        item.surface.material.needsUpdate = true;
        item.surface.visible = true;
        item.loaded = true;
      }
    } catch (error) {
      console.warn('[VOGALAB PhoneTunnel] Animated media failed', item.url, error);
      if (item.type === 'webp' && item.domImage) {
        item.domImage.removeAttribute('src');
        item.domSrc = '';
      }
    } finally {
      item.loading = false;
    }
  }

  rectOverlapRatio(a, b) {
    if (!a || !b) return 0;
    const left = Math.max(a.left, b.left);
    const top = Math.max(a.top, b.top);
    const right = Math.min(a.right, b.right);
    const bottom = Math.min(a.bottom, b.bottom);
    if (right <= left || bottom <= top) return 0;
    const intersection = (right - left) * (bottom - top);
    const areaA = Math.max(1, a.width * a.height);
    const areaB = Math.max(1, b.width * b.height);
    return intersection / Math.max(1, Math.min(areaA, areaB));
  }

  syncAnimatedMedia(time, dt) {
    if (!this.camera || !this.coversReady || !this.animatedCards.length || !this.renderRect) return;

    const activationDistance = Number(this.config.animationActivationDistance || 20);
    const rr = this.renderRect;
    const animationAllowed = this.allowAnimatedWebp === true;
    const minForward = 2.15; // Before a card reaches the near plane, hand back to the static cover.

    const candidates = animationAllowed ? this.animatedCards
      .map(item => {
        const projection = this.projectCardSurface(item.group);
        const forward = projection?.forward || 0;
        const distance = Math.abs(forward);
        item.projection = projection;
        return { item, distance, forward, projection };
      })
      .filter(entry => {
        const p = entry.projection;
        if (!p) return false;
        // Only animate cards safely in front of the camera. Near-plane projections are
        // deliberately excluded because they explode to huge DOM rectangles and caused
        // the olive/green full-screen flash seen when passing a card.
        if (entry.forward < minForward || entry.forward > activationDistance) return false;
        const onScreen = p.right > rr.x && p.left < rr.x + rr.w && p.bottom > rr.y && p.top < rr.y + rr.h;
        if (!onScreen || p.width < 90 || p.height < 60) return false;
        // A card that already fills almost the entire screen is about to pass the camera.
        // Fade its WebP back to the WebGL cover before its projected box can blow up.
        if (p.width > rr.w * 1.08 || p.height > rr.h * 1.08) return false;
        return true;
      })
      .sort((a, b) => a.forward - b.forward)
      : [];

    // Always allow up to two animated cards, including while the user is scrolling.
    // Do NOT switch to one solely because a wheel/touch scroll event is active; that old
    // rule repeatedly deactivated/reloaded WebPs and made them appear to pause while scrolling.
    const maxAnimated = this.mobileMq.matches ? Math.max(1, Math.min(2, Number(this.config.maxAnimatedMobile || 1))) : 2;
    const chosen = [];
    for (const entry of candidates) {
      if (chosen.length >= maxAnimated) break;
      if (!chosen.length) {
        chosen.push(entry);
        continue;
      }
      const first = chosen[0].projection;
      const overlap = this.rectOverlapRatio(first, entry.projection);
      const mobile = this.mobileMq.matches;
      const foregroundLarge = first.width > rr.w * (mobile ? .48 : .62) || first.height > rr.h * (mobile ? .62 : .76);
      const overlapLimit = mobile ? .08 : .22;
      if (!foregroundLarge && overlap < overlapLimit) chosen.push(entry);
    }

    const active = new Set(chosen.map(entry => entry.item));

    for (const item of this.animatedCards) {
      const shouldPlay = active.has(item) && this.visible && !document.hidden;
      item.active = shouldPlay;
      item.targetFade = shouldPlay ? 1 : 0;

      if (shouldPlay) {
        item.lastActiveAt = time;
        item.inactiveSince = 0;
        if (!item.loaded && !item.loading) this.ensureAnimatedLoaded(item);
      } else if (!item.inactiveSince) {
        item.inactiveSince = time;
      }

      item.fade = THREE.MathUtils.damp(item.fade || 0, item.targetFade, 13, dt);

      if (item.type === 'webp') {
        const image = item.domImage;
        if (!image) continue;
        image.style.opacity = item.loaded ? String(item.fade) : '0';
        image.style.visibility = item.loaded && item.fade > .005 ? 'visible' : 'hidden';

        // Keep the native WebP source attached while scrolling past/around the card.
        // This lets the browser's own animation clock continue uninterrupted. Only unload
        // after the card has been inactive for a while AND is genuinely far from the camera.
        if (!shouldPlay && item.fade < .01 && item.domSrc) {
          const projection = item.projection || this.projectCardSurface(item.group);
          const forward = projection?.forward ?? -999;
          const farAway = forward < -6 || forward > activationDistance + 10 || !projection;
          const inactiveLongEnough = time - (item.lastActiveAt || 0) > 1800;
          if (farAway && inactiveLongEnough) {
            image.removeAttribute('src');
            item.domSrc = '';
            item.loaded = false;
            item.loading = false;
            image.style.visibility = 'hidden';
            image.style.opacity = '0';
            image.style.clipPath = 'none';
            image.style.transform = 'translate3d(-9999px,-9999px,0)';
          }
        }
      } else if (item.video) {
        item.surface.material.opacity = item.fade;
        item.surface.visible = item.loaded && (item.fade > .005 || item.targetFade > 0);
        if (shouldPlay) {
          if (item.video.paused) item.video.play().catch(() => {});
        } else if (!item.video.paused) {
          item.video.pause();
        }
      }
    }
  }

  cover(tex, aspect) {
    const im = tex.image; if (!im) return;
    const w = im.videoWidth || im.naturalWidth || im.width || 1, h = im.videoHeight || im.naturalHeight || im.height || 1, a = w / h;
    tex.repeat.set(1, 1); tex.offset.set(0, 0);
    if (a > aspect) { tex.repeat.x = aspect / a; tex.offset.x = (1 - tex.repeat.x) / 2; }
    else { tex.repeat.y = a / aspect; tex.offset.y = (1 - tex.repeat.y) / 2; }
  }

  phoneScale(p) {
    const mobile = this.mobileMq.matches;
    const start = Number(mobile ? this.dataset.startScaleMobile : this.dataset.startScaleDesktop) || .55;
    const end = Number(mobile ? this.dataset.endScaleMobile : this.dataset.endScaleDesktop) || 4.5;

    // V4.9 MOBILE: no more long "border animation". On phones the device only does
    // one monotonic zoom IN, then hands off to the tunnel. It never shrinks/reverses.
    if (mobile) {
      const t = this.clamp(p);
      // Smooth ease-out: strong visible growth immediately, softer as it reaches fullscreen.
      const z = 1 - Math.pow(1 - t, 2.15);
      return this.lerp(start, end, z);
    }

    const exitStart = Number(this.dataset.screenExitStart) || .68;
    const t = this.clamp(p / Math.max(.001, exitStart));
    const delayed = Math.pow(t, 1.68);
    const ratio = Math.max(1.001, end / Math.max(.001, start));
    return start * Math.pow(ratio, delayed);
  }

  projectedScreenRect(scale) {
    const w = this.phone.offsetWidth, h = this.phone.offsetHeight;
    const topP = (Number(this.dataset.screenTop) || 3.6) / 100, bottomP = (Number(this.dataset.screenBottom) || 3.2) / 100, leftP = (Number(this.dataset.screenLeft) || 3.5) / 100, rightP = (Number(this.dataset.screenRight) || 3.5) / 100;
    const phoneLeft = this.sticky.clientWidth / 2 - (w * scale) / 2, phoneTop = this.sticky.clientHeight / 2 - (h * scale) / 2;
    const radius = parseFloat(getComputedStyle(this.host).getPropertyValue('--vpt-screen-radius')) || 42;
    return { x: phoneLeft + w * leftP * scale, y: phoneTop + h * topP * scale, w: w * (1 - leftP - rightP) * scale, h: h * (1 - topP - bottomP) * scale, radius: radius * scale };
  }

  scrollProgress() {
    const rect = this.getBoundingClientRect();
    return this.clamp(-rect.top / Math.max(this.offsetHeight - innerHeight, 1));
  }

  updateScroll() {
    this.scrollRaf = 0;
    if (this.dead || !this.sticky || !this.phone || !this.screen) return;

    const p = this.scrollProgress();
    const isMobile = this.mobileMq.matches;
    const phoneScrollVh = Math.max(1, Number(isMobile ? this.dataset.phoneScrollVhMobile : this.dataset.phoneScrollVh) || (isMobile ? 80 : 480));
    const perCardScrollVh = Math.max(1, Number(isMobile ? this.dataset.perCardScrollVhMobile : this.dataset.perCardScrollVh) || (isMobile ? 70 : 120));
    const tunnelScrollVh = Math.max(1, this.cards.length * perCardScrollVh);
    const phoneEnd = phoneScrollVh / (phoneScrollVh + tunnelScrollVh);
    const phoneP = this.range(p, 0, phoneEnd);

    // Mobile uses a short, direct zoom-to-fullscreen handoff. Desktop keeps the cinematic timings.
    const exitStart = isMobile ? .58 : (Number(this.dataset.screenExitStart) || .68);
    const exitEnd = isMobile ? .96 : Math.max(exitStart + .04, Number(this.dataset.screenExitEnd) || .80);
    // V4.8: tunnel travel starts as soon as the phone screen has completed its
    // fullscreen hand-off. The old build waited until 100% of the phone phase,
    // leaving a long dead-scroll section between fullscreen and tunnel motion.
    // Mobile: tunnel stays frozen until the phone has completely become fullscreen.
    // There is no overlap where the 3D card can appear to shrink while the phone border is still morphing.
    const tunnelStartPage = isMobile ? phoneEnd : phoneEnd * exitEnd;
    const tunnelP = this.range(p, tunnelStartPage, 1);
    this.phonePhaseProgress = phoneP;
    this.tunnelPhaseProgress = tunnelP;
    const scale = this.phoneScale(phoneP);
    const e4 = this.ease(this.range(phoneP, exitStart, exitEnd));

    // 手机外框始终是“空心框”，3D Canvas 从第一帧就在里面可见。
    this.phone.style.transform = `translate(-50%,-50%) scale(${scale.toFixed(5)})`;
    this.phone.style.opacity = String(1 - e4);

    const from = this.projectedScreenRect(scale);
    const W = Math.max(1, this.sticky.clientWidth);
    const H = Math.max(1, this.sticky.clientHeight);
    const target = { x: 0, y: 0, w: W, h: H, radius: 0 };
    const x = this.lerp(from.x, target.x, e4);
    const y = this.lerp(from.y, target.y, e4);
    const w = this.lerp(from.w, target.w, e4);
    const h = this.lerp(from.h, target.h, e4);
    const radius = this.lerp(Math.min(from.radius, 120), 0, e4);

    this.renderRect = { x, y, w, h, radius };

    const top = Math.max(0, y);
    const left = Math.max(0, x);
    const right = Math.max(0, W - (x + w));
    const bottom = Math.max(0, H - (y + h));
    const clip = `inset(${top.toFixed(2)}px ${right.toFixed(2)}px ${bottom.toFixed(2)}px ${left.toFixed(2)}px round ${Math.max(0, radius).toFixed(2)}px)`;
    this.screen.style.clipPath = clip;
    this.screen.style.webkitClipPath = clip;
    this.screen.classList.add('is-ready');
    this.screen.style.pointerEvents = e4 > .88 ? 'auto' : 'none';

    // 关键修复：手机放大阶段先保持在 Tunnel 开场视角。
    // 只有屏幕基本接管全屏后，才开始真正沿 Tunnel 前进，避免“刚全屏就跳到很深处”。
    // V3.7: tunnel travel has its own long scroll phase after the phone reaches fullscreen.
    // Each card receives an independent VH budget, so one mouse-wheel step can no longer
    // skip several pieces of content at once.
    // V4.8 mobile: do not smoothstep the tunnel at the start. smoothstep has a near-zero
    // initial slope, which made several finger swipes appear to do almost nothing.
    // Keep desktop's cinematic easing, but make mobile front-loaded / near-linear.
    const mobileResponse = Math.max(.75, Math.min(1, Number(this.dataset.mobileTunnelResponse) || .90));
    this.tunnelTarget = isMobile ? Math.pow(tunnelP, mobileResponse) : this.ease(tunnelP);
    // V4.3: the first animated WebP must NOT appear during the phone-zoom intro.
    // Keep static covers until the phone has fully handed off to the tunnel.
    this.allowAnimatedWebp = tunnelP > 0.025;

    if (this.intro) {
      const fadeEnd = Number(this.dataset.introFadeEnd) || .30;
      const fadeStart = Math.max(0, fadeEnd - .14);
      const introT = this.ease(this.range(phoneP, fadeStart, fadeEnd));
      this.intro.style.opacity = String(1 - introT);
      this.intro.style.transform = `translate(-50%,${(-20 * introT).toFixed(2)}px)`;
      this.intro.style.visibility = introT > .995 ? 'hidden' : 'visible';
    }
    if (this.hint) this.hint.style.opacity = String(this.clamp(1 - phoneP / .18));
    if (this.scrollDebug) this.scrollDebug.textContent = p.toFixed(3);
    if (this.scaleDebug) this.scaleDebug.textContent = scale.toFixed(3);
    if (this.tunnelDebug) this.tunnelDebug.textContent = this.tunnelTarget.toFixed(3);
    if (this.phaseDebug) {
      this.phaseDebug.textContent = p >= phoneEnd ? 'TUNNEL' : phoneP < .16 ? '1' : phoneP < .55 ? '2' : phoneP < exitStart ? '3' : phoneP < exitEnd ? '4' : '5';
    }
  }

  scheduleScroll() { if (!this.scrollRaf && !this.dead) this.scrollRaf = requestAnimationFrame(() => this.updateScroll()); }

  resetToStartIfNeeded() {
    if (window.Shopify?.designMode) return;
    if (this.dataset.resetOnLoad !== 'true') return;

    // Disable browser/BFCache restoration for this dedicated scroll-story page.
    try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (_) {}

    const reset = () => {
      if (this.dead) return;
      const p = this.scrollProgress();
      if (p <= .025) return;
      const top = window.scrollY + this.getBoundingClientRect().top;
      window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'auto' });
      this.tunnelTarget = 0;
      this.tunnelProgress = 0;
      this.updateScroll();
    };

    // Browser scroll restoration can run after modules execute, so check twice.
    requestAnimationFrame(() => requestAnimationFrame(reset));
    setTimeout(reset, 160);
    window.addEventListener('pageshow', () => setTimeout(reset, 40), { once: true, signal: this.abort.signal });
  }

  bindEvents() {
    const signal = this.abort.signal;
    addEventListener('scroll', () => {
      this.isScrolling = true;
      clearTimeout(this.scrollIdleTimer);
      this.scrollIdleTimer = setTimeout(() => { this.isScrolling = false; }, 150);
      this.scheduleScroll();
    }, { passive: true, signal });
    addEventListener('resize', () => this.scheduleScroll(), { passive: true, signal });
    this.addEventListener('pointermove', e => {
      const rr = this.renderRect;
      if (!rr || rr.w < 2 || rr.h < 2) return;
      if (e.clientX < rr.x || e.clientX > rr.x + rr.w || e.clientY < rr.y || e.clientY > rr.y + rr.h) {
        this.pointer = null;
        this.mouse.tx = 0;
        this.mouse.ty = 0;
        return;
      }
      this.mouse.tx = ((e.clientX - rr.x) / rr.w) * 2 - 1;
      this.mouse.ty = -(((e.clientY - rr.y) / rr.h) * 2 - 1);
      this.pointer = { x: this.mouse.tx, y: this.mouse.ty };
    }, { signal });
    this.stage.addEventListener('click', e => this.click(e), { signal });
    // Shopify Theme Editor used to auto-scroll this very tall storytelling section
    // whenever a media block was selected. That can make the preview reopen near the end
    // and look as if the phone intro is broken. Block selection must never change page scroll.
    this.addEventListener('shopify:block:select', () => {
      this.scheduleScroll();
    }, { signal });
    this.observer = new IntersectionObserver(entries => { this.visible = entries[0]?.isIntersecting ?? true; }, { rootMargin: '80% 0px 80% 0px' });
    this.observer.observe(this.host);
    document.addEventListener('visibilitychange', () => { if (document.hidden) { for (const item of this.animatedCards) if (item.video && !item.video.paused) item.video.pause(); } }, { signal });
  }

  resize(force = false) {
    if (!this.renderer || !this.camera || !this.sticky) return;
    const w = Math.max(2, Math.round(this.sticky.clientWidth));
    const h = Math.max(2, Math.round(this.sticky.clientHeight));
    if (!force && Math.abs(w - (this.lastW || 0)) < 4 && Math.abs(h - (this.lastH || 0)) < 4) return;
    this.lastW = w;
    this.lastH = h;
    this.renderer.setSize(w, h, false);
    // Projection aspect is updated per-frame from the visible phone-screen scissor.
  }

  hoverUpdate(dt) {
    if (!this.pointer || !this.camera) return;
    const ray = new THREE.Raycaster(); ray.setFromCamera(new THREE.Vector2(this.pointer.x, this.pointer.y), this.camera);
    const hit = ray.intersectObjects(this.clickTargets)[0]; const active = hit ? this.cardGroups.find(g => g.children.includes(hit.object)) : null;
    if (this.hovered && this.hovered !== active) this.hovered.userData.hoverT = 0;
    this.hovered = active; if (active) active.userData.hoverT = 1;
    this.renderer.domElement.style.cursor = active ? 'pointer' : 'default';
    for (const g of this.cardGroups) { const u = g.userData; u.hover = THREE.MathUtils.damp(u.hover || 0, u.hoverT || 0, 8, dt); u.pop = Math.max(0, (u.pop || 0) - dt * 2.8); const target = 1 + u.hover * .07 + u.pop * .13; g.scale.setScalar(THREE.MathUtils.damp(g.scale.x, target, 10, dt)); }
  }

  updateCardCopies() {
    if (!this.cardCopyEls?.length || !this.camera || !this.renderRect) return;
    const rr = this.renderRect;
    const isMobile = this.mobileMq.matches;
    const minWidth = Number(isMobile ? this.config.cardCopyMinWidthMobile : this.config.cardCopyMinWidth) || (isMobile ? 140 : 320);
    const fadePx = Math.max(20, Number(isMobile ? this.config.cardCopyFadePxMobile : this.config.cardCopyFadePx) || (isMobile ? 60 : 100));
    const globalScale = Number(this.config.cardTitleScale || 100) / 100;
    const gapPx = Math.max(4, Number(isMobile ? this.config.cardCopyGapMobile : this.config.cardCopyGap) || (isMobile ? 10 : 18));
    const tunnelStartGate = this.clamp(Number(isMobile ? this.config.cardCopyTunnelStartMobile : this.config.cardCopyTunnelStart) / 100 || (isMobile ? .02 : .04), 0, .5);
    const maxViewportRatio = this.clamp(Number(isMobile ? this.config.cardCopyMaxViewportMobile : this.config.cardCopyMaxViewport) / 100 || (isMobile ? .85 : .50), .25, .95);

    for (let i = 0; i < this.cardCopyEls.length; i++) {
      const el = this.cardCopyEls[i];
      const group = this.cardGroups[i];
      if (!el || !group) {
        if (el) {
          el.style.opacity = '0';
          el.style.visibility = 'hidden';
        }
        continue;
      }

      group.updateMatrixWorld(true);
      const width = group.userData.cardWidth || 4.6;
      const height = group.userData.cardHeight || 3.07;

      // V3.8: project the REAL four card corners, then attach the DOM copy
      // directly above the projected card frame. This prevents the title
      // drifting to the top of the viewport when perspective changes.
      const corners = [
        new THREE.Vector3(-width / 2,  height / 2, .12),
        new THREE.Vector3( width / 2,  height / 2, .12),
        new THREE.Vector3(-width / 2, -height / 2, .12),
        new THREE.Vector3( width / 2, -height / 2, .12)
      ];

      const points = corners.map(point => {
        group.localToWorld(point);
        const ndc = point.project(this.camera);
        return {
          ndc,
          x: rr.x + (ndc.x * .5 + .5) * rr.w,
          y: rr.y + (-ndc.y * .5 + .5) * rr.h
        };
      });

      const topLeft = points[0];
      const topRight = points[1];
      const allXs = points.map(p => p.x);
      const allYs = points.map(p => p.y);
      const cardLeft = Math.min(...allXs);
      const cardRight = Math.max(...allXs);
      const cardTop = Math.min(...allYs);
      const cardBottom = Math.max(...allYs);
      const pxWidth = Math.max(0, cardRight - cardLeft);
      const pxHeight = Math.max(0, cardBottom - cardTop);
      const topCenterX = (topLeft.x + topRight.x) * .5;
      const topEdgeY = (topLeft.y + topRight.y) * .5;
      const signed = this.camera.position.z - group.position.z;

      let alpha = this.ease(this.clamp((pxWidth - minWidth) / fadePx));

      // V4.5: never reveal card copy during the phone-to-fullscreen hand-off.
      // It may only appear after the tunnel has genuinely begun moving.
      if ((this.tunnelPhaseProgress || 0) < tunnelStartGate) alpha = 0;

      // Fade copy back out before the card becomes an oversized foreground object.
      // This prevents the title from appearing "too early" on the first giant card.
      const maxCopyCardWidth = rr.w * maxViewportRatio;
      if (pxWidth > maxCopyCardWidth) {
        const over = (pxWidth - maxCopyCardWidth) / Math.max(60, rr.w * .12);
        alpha *= 1 - this.ease(this.clamp(over));
      }

      // Hide copy when card is behind the camera or its projected top edge
      // has already exited the visible phone/fullscreen viewport.
      const topSafe = rr.y + (isMobile ? 6 : 12);
      const bottomSafe = rr.y + rr.h - (isMobile ? 6 : 12);
      const sideVisible = cardRight > rr.x && cardLeft < rr.x + rr.w;
      const verticalVisible = cardBottom > rr.y && cardTop < rr.y + rr.h;
      const topHasRoom = topEdgeY > topSafe + gapPx + (isMobile ? 7 : 18);
      if (signed < -1.1 || !sideVisible || !verticalVisible || !topHasRoom) alpha = 0;

      // On mobile there is far less headroom above a portrait card. Use a
      // shorter fade zone so the title can become visible instead of being
      // mathematically impossible (desktop min-width > mobile hide-width).
      const edgeRoom = topEdgeY - topSafe - gapPx;
      alpha *= this.clamp((edgeRoom - (isMobile ? 7 : 18)) / (isMobile ? 42 : 70));

      const sizeScale = this.clamp(pxWidth / (isMobile ? 360 : 560), isMobile ? .68 : .72, isMobile ? 1.05 : 1.18) * globalScale;
      const maxCopyWidth = Math.max(isMobile ? 132 : 180, Math.min(pxWidth, rr.w * (isMobile ? .86 : .62)));

      el.style.left = `${topCenterX.toFixed(2)}px`;
      el.style.top = `${(topEdgeY - gapPx).toFixed(2)}px`;
      el.style.width = `${maxCopyWidth.toFixed(2)}px`;
      el.style.opacity = alpha.toFixed(3);
      el.style.visibility = alpha > .01 ? 'visible' : 'hidden';
      el.style.transform = `translate(-50%,-100%) scale(${sizeScale.toFixed(3)})`;
    }
  }

  projectCardSurface(group, surfaceScaleX = .90, surfaceScaleY = .86) {
    if (!group || !this.camera || !this.renderRect) return null;
    const rr = this.renderRect;
    group.updateMatrixWorld(true);
    this.camera.updateMatrixWorld(true);

    const width = (group.userData.cardWidth || 4.6) * surfaceScaleX;
    const height = (group.userData.cardHeight || 3.07) * surfaceScaleY;
    const center = new THREE.Vector3(0, 0, .105);
    group.localToWorld(center);
    const viewCenter = center.clone().applyMatrix4(this.camera.matrixWorldInverse);
    const forward = -viewCenter.z;
    if (!Number.isFinite(forward) || forward <= .01) return null;

    const local = [
      new THREE.Vector3(-width / 2,  height / 2, .105),
      new THREE.Vector3( width / 2,  height / 2, .105),
      new THREE.Vector3( width / 2, -height / 2, .105),
      new THREE.Vector3(-width / 2, -height / 2, .105)
    ];
    const points = local.map(point => {
      group.localToWorld(point);
      const ndc = point.project(this.camera);
      return {
        x: rr.x + (ndc.x * .5 + .5) * rr.w,
        y: rr.y + (-ndc.y * .5 + .5) * rr.h,
        z: ndc.z
      };
    });
    if (points.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y) || !Number.isFinite(p.z))) return null;

    const xs = points.map(p => p.x), ys = points.map(p => p.y);
    const left = Math.min(...xs), right = Math.max(...xs);
    const top = Math.min(...ys), bottom = Math.max(...ys);
    const projectedWidth = right - left;
    const projectedHeight = bottom - top;
    if (!Number.isFinite(projectedWidth) || !Number.isFinite(projectedHeight) || projectedWidth <= 0 || projectedHeight <= 0) return null;

    return { points, left, right, top, bottom, width: projectedWidth, height: projectedHeight, rr, forward };
  }

  homographyMatrix(points, sourceWidth, sourceHeight) {
    const p0 = points[0], p1 = points[1], p2 = points[2], p3 = points[3];
    const dx1 = p1.x - p2.x;
    const dx2 = p3.x - p2.x;
    const dx3 = p0.x - p1.x + p2.x - p3.x;
    const dy1 = p1.y - p2.y;
    const dy2 = p3.y - p2.y;
    const dy3 = p0.y - p1.y + p2.y - p3.y;

    let g = 0, h = 0;
    const det = dx1 * dy2 - dx2 * dy1;
    if (Math.abs(dx3) > 1e-5 || Math.abs(dy3) > 1e-5) {
      if (Math.abs(det) < 1e-6) return null;
      g = (dx3 * dy2 - dx2 * dy3) / det;
      h = (dx1 * dy3 - dx3 * dy1) / det;
    }

    const a = p1.x - p0.x + g * p1.x;
    const b = p3.x - p0.x + h * p3.x;
    const c = p0.x;
    const d = p1.y - p0.y + g * p1.y;
    const e = p3.y - p0.y + h * p3.y;
    const f = p0.y;

    const sw = Math.max(1, sourceWidth);
    const sh = Math.max(1, sourceHeight);
    const A = a / sw;
    const B = b / sh;
    const D = d / sw;
    const E = e / sh;
    const G = g / sw;
    const H = h / sh;

    return `matrix3d(${A},${D},0,${G},${B},${E},0,${H},0,0,1,0,${c},${f},0,1)`;
  }

  updateWebpOverlays(time = performance.now()) {
    if (!this.webpLayer || !this.animatedCards?.length || !this.camera || !this.renderRect) return;

    // Stable 30fps layout updates whether scrolling or idle. The native WebP itself is
    // NOT time-stepped by this code, so its encoded frame timing keeps running continuously.
    const interval = this.mobileMq.matches ? (this.isScrolling ? 66 : 42) : 33;
    if (time - this.lastWebpLayoutAt < interval) return;
    this.lastWebpLayoutAt = time;

    for (const item of this.animatedCards) {
      if (item.type !== 'webp' || !item.domImage) continue;
      const image = item.domImage;
      if (!this.allowAnimatedWebp || !item.loaded || item.fade <= .005) {
        image.style.visibility = 'hidden';
        continue;
      }

      const projection = this.projectCardSurface(item.group);
      item.projection = projection;
      if (!projection || projection.forward < 2.0) {
        image.style.visibility = 'hidden';
        continue;
      }

      const { rr, right, bottom, left, top, width, height, points } = projection;
      const visible = right > rr.x && left < rr.x + rr.w && bottom > rr.y && top < rr.y + rr.h;
      if (!visible || width < 2 || height < 2 || width > rr.w * 1.10 || height > rr.h * 1.10) {
        image.style.visibility = 'hidden';
        continue;
      }

      // Use an axis-aligned DOM box with object-fit:cover, then clip that box to the
      // projected 3D quadrilateral. This keeps the source WebP's aspect ratio intact,
      // prevents the first card from stretching, and cannot explode into a giant full-screen
      // rectangle when a card approaches the camera.
      const rel = points.map(p => ({
        x: this.clamp((p.x - left) / Math.max(1, width) * 100, -8, 108),
        y: this.clamp((p.y - top) / Math.max(1, height) * 100, -8, 108)
      }));
      const polygon = `polygon(${rel.map(p => `${p.x.toFixed(2)}% ${p.y.toFixed(2)}%`).join(',')})`;

      image.style.width = `${width.toFixed(2)}px`;
      image.style.height = `${height.toFixed(2)}px`;
      image.style.transform = `translate3d(${left.toFixed(2)}px,${top.toFixed(2)}px,0)`;
      image.style.clipPath = polygon;
      image.style.webkitClipPath = polygon;
      image.style.opacity = String(item.fade);
      image.style.visibility = 'visible';
    }
  }

  tick(t) {
    if (this.dead) return;
    this.frame = requestAnimationFrame(x => this.tick(x));
    if (!this.visible || document.hidden || !this.camera) return;

    // Keep WebGL at a stable ~30fps in both scrolling and idle states. Native animated
    // WebPs remain independent DOM images and keep their own frame clock while scrolling.
    const frameInterval = this.mobileMq.matches ? 36 : 32;
    if (t - this.lastSceneRenderAt < frameInterval) return;
    this.lastSceneRenderAt = t;
    const dt = Math.min((t - this.last) / 1000, .05); this.last = t;

    this.tunnelProgress = this.reduceMotionPreferred ? this.tunnelTarget : THREE.MathUtils.damp(this.tunnelProgress, this.tunnelTarget, this.mobileMq.matches ? 12 : 6, dt);
    this.mouse.x = THREE.MathUtils.damp(this.mouse.x, this.mouse.tx, 6, dt); this.mouse.y = THREE.MathUtils.damp(this.mouse.y, this.mouse.ty, 6, dt);
    for (const animation of this.anim) animation(t * .001, dt);

    const spacing = Number(this.mobileMq.matches ? this.config.cardSpacingMobile : this.config.cardSpacing) || (this.mobileMq.matches ? 12 : 8);
    const travel = (this.cards.length - 1) * spacing + 6.7;
    const bx = .15 * Math.sin(this.tunnelProgress * Math.PI * 2), bz = -2.2 - this.tunnelProgress * travel;
    this.camera.position.set(bx + this.mouse.x * .45, 3.1 + this.mouse.y * .24, bz);
    this.camera.lookAt(bx * .4 + this.mouse.x * .8, 3.15 - this.mouse.y * .35, this.camera.position.z - 15);

    if (!this.isScrolling) this.hoverUpdate(dt);
    for (const group of this.cardGroups) { const d = this.camera.position.z - group.position.z; const pass = 1 - THREE.MathUtils.smoothstep(d, 1, 5); group.position.x = group.userData.baseX + group.userData.side * group.userData.clearance * pass; }

    // DOM card-copy positions are updated after the active projection matrix is set below.


    // Clear the fixed viewport canvas to transparent, then render only inside the
    // projected phone-screen rectangle using WebGL scissor. This is the key flicker fix.
    const W = this.lastW || this.sticky.clientWidth;
    const H = this.lastH || this.sticky.clientHeight;
    const rr = this.renderRect || { x: 0, y: 0, w: W, h: H, radius: 0 };
    const left = Math.max(0, rr.x);
    const top = Math.max(0, rr.y);
    const right = Math.min(W, rr.x + rr.w);
    const bottom = Math.min(H, rr.y + rr.h);
    const rw = Math.max(0, right - left);
    const rh = Math.max(0, bottom - top);

    this.renderer.setScissorTest(false);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.clear(true, true, true);

    if (rw > 1 && rh > 1) {
      const glY = H - bottom;
      this.renderer.setViewport(left, glY, rw, rh);
      this.renderer.setScissor(left, glY, rw, rh);
      this.renderer.setScissorTest(true);
      this.renderer.setClearColor(this.scene.background || new THREE.Color(this.config.worldColor || '#f7f1e6'), 1);
      this.renderer.clear(true, true, true);

      const desiredAspect = rw / rh;
      const baseFov = this.config.fov || 48;
      // 连续插值，不再在 aspect=.8 / 1.1 两个阈值突然跳 FOV。
      // 竖屏时略宽，随着屏幕展开为横向全屏平滑回到设定 FOV。
      const aspectT = this.clamp((desiredAspect - .48) / (1.35 - .48));
      const smoothT = aspectT * aspectT * (3 - 2 * aspectT);
      const desiredFov = this.lerp(Math.min(72, baseFov + 16), baseFov, smoothT);
      if (Math.abs(this.camera.aspect - desiredAspect) > .001 || Math.abs(this.camera.fov - desiredFov) > .05) {
        this.camera.aspect = desiredAspect;
        this.camera.fov = desiredFov;
        this.camera.updateProjectionMatrix();
      }
      this.camera.updateMatrixWorld(true);
      this.updateCardCopies();
      this.updateWebpOverlays(t);
      this.renderer.render(this.scene, this.camera);
      this.renderer.setScissorTest(false);
    } else if (this.webpLayer) {
      for (const item of this.animatedCards) {
        if (item.type === 'webp' && item.domImage) item.domImage.style.visibility = 'hidden';
      }
    }

    if (this.progressBar) this.progressBar.style.transform = `scaleX(${this.tunnelProgress})`;
    if (this.tunnelLabel) { const current = Math.min(this.cards.length - 1, Math.round(this.tunnelProgress * (this.cards.length - 1))); this.tunnelLabel.textContent = this.cards[current]?.title || ''; }
    this.syncAnimatedMedia(t, dt);
  }

  click(e) {
    if (!this.renderer || !this.camera) return;
    const rr = this.renderRect; if (!rr || rr.w < 2 || rr.h < 2) return;
    const ray = new THREE.Raycaster();
    ray.setFromCamera(new THREE.Vector2((e.clientX - rr.x) / rr.w * 2 - 1, -((e.clientY - rr.y) / rr.h) * 2 + 1), this.camera);
    const hit = ray.intersectObjects(this.clickTargets)[0]; if (!hit) return;
    const group = this.cardGroups.find(g => g.children.includes(hit.object)); if (group) group.userData.pop = 1;
    if (hit.object.userData.link) { try { const url = new URL(hit.object.userData.link, location.href); if (['https:', 'http:'].includes(url.protocol)) location.assign(url.href); } catch (_) {} }
  }

  fail(error) {
    if (this.dead) return;
    console.error('[VOGALAB PhoneTunnel] initialization failed', error);
    if (this.status) this.status.textContent = '3D 初始化失败：' + (error?.message || String(error));
    if (this.fallback) this.fallback.hidden = false;
    if (this.debug3d) this.debug3d.textContent += '\nFATAL: ' + (error?.message || String(error));
  }

  disposeResources() { for (const resource of this.resources) resource?.dispose?.(); this.resources.clear(); }

  disconnectedCallback() {
    this.dead = true;
    cancelAnimationFrame(this.scrollRaf); cancelAnimationFrame(this.frame); clearTimeout(this.scrollIdleTimer);
    this.abort?.abort(); this.observer?.disconnect(); this.resizeObserver?.disconnect();
    for (const item of this.animatedCards) {
      if (item.video) { item.video.pause(); item.video.removeAttribute('src'); item.video.load(); }
      if (item.domImage) { item.domImage.removeAttribute('src'); item.domImage.remove(); }
    }
    this.disposeResources(); this.draco?.dispose?.(); this.renderer?.dispose?.(); this.renderer?.domElement?.remove(); this.started = false;
  }
}




/* Mobile-only controller. Inherits the real GLB loader, scene, lights and card meshes.
 * The desktop controller above is kept byte-for-byte unchanged.
 * Only composition, phone-entry geometry and small-screen scroll mapping differ. */
class MobilePhoneTunnel extends DesktopPhoneTunnel {
  constructor(host) { super(host); this.mobileTop = 0; this.lastMobileMeasure = ''; }

  async init() {
    this.host.dataset.mobileEngine = 'three-glb';
    await super.init();
    if (!this.dead) this.log('手机端：真实 GLB + 手机放大开场已恢复');
  }

  setupRenderer() {
    super.setupRenderer();
    this.camera.far = Math.max(150, this.cards.length * (Number(this.config.cardSpacingMobile) || 12) + 30);
    this.camera.updateProjectionMatrix();
    this.mobileCaption = this.querySelector('[data-vpt-mobile-caption]');
    this.mobileCaptionTitle = this.querySelector('[data-vpt-mobile-title]');
    this.mobileCaptionLabel = this.querySelector('[data-vpt-mobile-label]');
    this.mobileCaptionDescription = this.querySelector('[data-vpt-mobile-description]');
    this.mobileFooter = this.querySelector('[data-vpt-mobile-footer]');
    this.mobileCounter = this.querySelector('[data-vpt-mobile-counter]');
    this.mobileFill = this.querySelector('[data-vpt-mobile-fill]');
  }

  async safeLoad(url, label) {
    const model = await super.safeLoad(url, label);
    if (this.dead) { this.disposeResources(); return null; }
    if (!this.dead) this.host.dataset[label === 'Tunnel GLB' ? 'tunnelAsset' : 'cardAsset'] = model ? 'glb-loaded' : 'fallback';
    return model;
  }

  measureMobile() {
    const width = this.sticky.clientWidth;
    if (this.measuredWidth !== width) {
      const explicit = Number(this.config.mobileGalleryTop || 0);
      let top = explicit;
      if (!explicit) {
        // Inspect only this document's header; do not change theme globals or scroll position.
        document.querySelectorAll('sticky-header, #shopify-section-header, .section-header, .shopify-section-group-header-group').forEach(el => {
          if (this.host.contains(el)) return;
          const r = el.getBoundingClientRect();
          if (r.height <= 0 || r.height > 160) return;
          let n = el, fixed = false;
          for (let k = 0; n && k < 4; k++, n = n.parentElement) {
            if (['fixed','sticky'].includes(getComputedStyle(n).position)) { fixed = true; break; }
          }
          if (fixed) top = Math.max(top, Math.round(r.height));
        });
      }
      this.mobileTop = this.clamp(top, 0, 160);
      this.host.style.setProperty('--vpt-mobile-top', this.mobileTop + 'px');
      this.measuredWidth = width;
    }
    const W = Math.max(1, this.sticky.clientWidth), H = Math.max(1, this.sticky.clientHeight);
    const introVH = Math.max(60, Number(this.dataset.mobilePhoneTravel) || 110);
    const cardVH = this.clamp(Number(this.config.mobileStoryStep) || 240, 160, 400);
    const key = `${W}:${H}:${this.cards.length}:${introVH}:${cardVH}`;
    if (key !== this.lastMobileMeasure) {
      this.lastMobileMeasure = key;
      this.phonePixels = H * introVH / 100;
      this.cardPixels = H * cardVH / 100;
      this.tunnelPixels = this.cards.length * this.cardPixels;
      this.mobileTrack = this.querySelector('.vpt-scroll-space');
      this.mobileTrack.style.height = `${Math.ceil(H + this.phonePixels + this.tunnelPixels)}px`;
      this.mobileStart = this.clamp(Number(this.dataset.mobilePhoneScale) / 100 || .6, .4, .8);
      this.basePhoneWidth = Math.min(W * .76, H * .60 / (19.5 / 9) / this.mobileStart);
      this.phone.style.width = `${this.basePhoneWidth}px`;
    }
    return { W, H };
  }

  updateScroll() {
    this.scrollRaf = 0;
    if (this.dead || !this.sticky || !this.phone || !this.screen) return;
    const { W, H } = this.measureMobile();
    const distance = Math.max(0, this.mobileTop - this.getBoundingClientRect().top);
    const p = this.clamp(distance / this.phonePixels);
    this.phonePhaseProgress = p;
    this.tunnelPhaseProgress = this.clamp((distance - this.phonePixels) / Math.max(1, this.tunnelPixels));
    this.tunnelTarget = this.mobileReadingProgress(this.tunnelPhaseProgress);
    this.allowAnimatedWebp = p >= 1;

    const bw = this.basePhoneWidth, bh = bw * 19.5 / 9;
    const l = (Number(this.dataset.screenLeft) || 3.5) / 100;
    const r = (Number(this.dataset.screenRight) || 3.5) / 100;
    const t = (Number(this.dataset.screenTop) || 3.6) / 100;
    const b = (Number(this.dataset.screenBottom) || 3.2) / 100;
    const sw = bw * (1 - l - r), sh = bh * (1 - t - b);
    const end = Math.max(this.mobileStart, W / sw, H / sh) * 1.015;
    // Grow once to the actual viewport; never overshoot to 3x and shrink back to 1x.
    const scale = this.mobileStart * Math.pow(end / this.mobileStart, p);
    const centerY = H * (.60 - .10 * p);
    const phoneLeft = W / 2 - bw * scale / 2;
    const phoneTop = centerY - bh * scale / 2;
    const raw = { x: phoneLeft + bw * l * scale, y: phoneTop + bh * t * scale, w: sw * scale, h: sh * scale };
    const left = this.clamp(raw.x, 0, W), top = this.clamp(raw.y, 0, H);
    const right = this.clamp(raw.x + raw.w, 0, W), bottom = this.clamp(raw.y + raw.h, 0, H);
    const handoff = this.ease(this.range(p, .84, 1));
    const radius = p >= 1 ? 0 : Math.min(42 * scale, 60) * (1 - handoff);
    this.renderRect = p >= 1 ? {x:0, y:0, w:W, h:H, radius:0} : {x:left, y:top, w:right-left, h:bottom-top, radius};
    const rr = this.renderRect;
    this.phone.style.left = `${W / 2}px`;
    this.phone.style.top = `${centerY}px`;
    this.phone.style.transform = `translate(-50%,-50%) scale(${scale})`;
    this.phone.style.opacity = String(1 - handoff);
    const clip = `inset(${rr.y}px ${Math.max(0,W-rr.x-rr.w)}px ${Math.max(0,H-rr.y-rr.h)}px ${rr.x}px round ${radius}px)`;
    this.screen.style.clipPath = clip;
    this.screen.style.webkitClipPath = clip;
    this.screen.classList.add('is-ready');
    this.screen.style.pointerEvents = p >= 1 ? 'auto' : 'none';
    if (this.intro) {
      const endFade = Number(this.dataset.introFadeEnd) || .30;
      const fade = this.ease(this.range(p, Math.max(0,endFade-.14), endFade));
      this.intro.style.opacity = String(1-fade);
      this.intro.style.visibility = fade >= .999 ? 'hidden' : 'visible';
      this.intro.style.transform = `translate(-50%,${-12*fade}px)`;
    }
    if (this.hint) this.hint.style.opacity = String(1-this.ease(this.range(p,0,.25)));
    if (this.mobileFooter) this.mobileFooter.style.opacity = p >= 1 ? '1' : '0';
    if (this.scrollDebug) this.scrollDebug.textContent = this.tunnelPhaseProgress.toFixed(3);
    if (this.phaseDebug) this.phaseDebug.textContent = p >= 1 ? 'TUNNEL' : 'PHONE';
    if (this.scaleDebug) this.scaleDebug.textContent = scale.toFixed(3);
    if (this.tunnelDebug) this.tunnelDebug.textContent = this.tunnelTarget.toFixed(3);
    this.host.dataset.mobilePhase = p >= 1 ? 'tunnel' : 'phone';
  }

  // Match scroll space to camera travel: each card has a 60% reading plateau.
  // Boundaries share a camera position, so forward and reverse scroll stay continuous.
  mobileReadingProgress(progress) {
    const count = this.cards.length;
    if (!count) return 0;
    const spacing = Number(this.config.cardSpacingMobile) || 12;
    const travel = (count - 1) * spacing + 3.2;
    const phase = this.clamp(progress) * count;
    const index = Math.min(count - 1, Math.floor(phase));
    const local = Math.min(1, phase - index);
    // At the plateau the card is about 7 scene units ahead, fitting the viewport.
    const stop = index * spacing + .8;
    const begin = index === 0 ? 0 : stop - spacing / 2;
    const end = index === count - 1 ? travel : stop + spacing / 2;
    let distance = stop;
    if (local < .2) distance = this.lerp(begin, stop, this.ease(local / .2));
    else if (local > .8) distance = this.lerp(stop, end, this.ease((local - .8) / .2));
    return this.clamp(distance / travel);
  }

  // Warm network bytes only. Decoding remains restricted to the active card.
  prefetchNextMobileWebp(index) {
    if (this.dead || !this.coversReady || !this.visible || document.hidden) return;
    const item = this.animatedCards.find(a => a.index === index && a.type === 'webp');
    if (!item || !item.url || item.loaded || this.mobilePrefetchBusy) return;
    this.mobilePrefetched ||= new Set();
    if (this.mobilePrefetched.has(item.url)) return;
    this.mobilePrefetched.add(item.url);
    this.mobilePrefetchBusy = true;
    fetch(item.url, { cache: 'force-cache', signal: this.abort.signal })
      .then(response => {
        if (!response.ok) throw new Error('WebP prefetch');
        return response.arrayBuffer();
      })
      .catch(() => { this.mobilePrefetched.delete(item.url); })
      .finally(() => { this.mobilePrefetchBusy = false; });
  }

  resetToStartIfNeeded() {
    // Never jump to this below-the-fold section from a product hero or a hash link.
    // Browser/back-navigation restoration is preserved; reverse scrolling restores the phone.
  }

  bindEvents() {
    const signal = this.abort.signal;
    addEventListener('scroll', () => this.scheduleScroll(), {passive:true, signal});
    addEventListener('resize', () => this.scheduleScroll(), {passive:true, signal});
    addEventListener('pageshow', () => this.scheduleScroll(), {signal});
    this.stage.addEventListener('click', e => this.click(e), {signal});
    this.addEventListener('shopify:block:select', () => this.scheduleScroll(), {signal});
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries.some(e=>e.isIntersecting);
      if (!this.visible) this.stopMobileMotion();
    }, {rootMargin:'15% 0px'});
    this.observer.observe(this.host);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopMobileMotion();
    }, {signal});
  }

  async addCard(data, i, frame, narrow) {
    const job = super.addCard(data, i, frame, false);
    const group = this.cardGroups[i];
    if (group) { group.rotation.y = i%2 ? -.045 : .045; group.position.y = 2.5; }
    const item = this.animatedCards.find(a=>a.index===i);
    if (item?.domImage) {
      const wrapper = document.createElement('div');
      wrapper.className = 'vpt-mobile-motion';
      wrapper.style.width = '640px'; wrapper.style.height = `${640/item.aspect}px`;
      this.webpLayer.appendChild(wrapper); wrapper.appendChild(item.domImage);
      item.domWrapper = wrapper;
    }
    return job;
  }

  resize(force = false) {
    if (!this.renderer || !this.sticky) return;
    const w = Math.round(this.sticky.clientWidth), h = Math.round(this.sticky.clientHeight);
    if (w === this.lastW && h === this.lastH) return;
    super.resize(true);
    this.scheduleScroll();
  }

  projectCardSurface(group, sx=.90, sy=.86) {
    if (!group || !this.camera) return null;
    group.updateMatrixWorld(true); this.camera.updateMatrixWorld(true);
    const w=(group.userData.cardWidth||4.6)*sx/2, h=(group.userData.cardHeight||2.7)*sy/2;
    // All corners must be in front of the near plane BEFORE projection.
    for (const [x,y] of [[-w,h],[w,h],[w,-h],[-w,-h]]) {
      const v=new THREE.Vector3(x,y,.105);
      group.localToWorld(v); v.applyMatrix4(this.camera.matrixWorldInverse);
      if (-v.z < .4) return null;
    }
    return super.projectCardSurface(group,sx,sy);
  }

  updateCardCopies() {
    this.cardCopyEls?.forEach(el=>{el.style.opacity='0';el.style.visibility='hidden';});
    if (!this.mobileCaption || !this.renderRect) return;
    const rr=this.renderRect;
    const entries=this.cardGroups.map((g,i)=>({g,i,p:this.projectCardSurface(g,1,1)}))
      .filter(e=> e.p && e.p.forward>3.5 && e.p.right>0 && e.p.left<rr.w && e.p.width>rr.w*.27 && e.p.width<rr.w*1.28)
      .sort((a,b)=>a.p.forward-b.p.forward);
    const entry=entries[0];
    this.activeMobileCard=entry?.i ?? Math.min(this.cards.length-1,Math.max(0,Math.round(((-2.2-this.camera.position.z)-3.5)/(Number(this.config.cardSpacingMobile)||12))));
    if (this.mobileCounter) this.mobileCounter.textContent=`${String(this.activeMobileCard+1).padStart(2,'0')} / ${String(this.cards.length).padStart(2,'0')}`;
    if (this.mobileFill) this.mobileFill.style.transform=`scaleX(${(this.activeMobileCard+1)/Math.max(1,this.cards.length)})`;
    if (this.mobileFooter) this.mobileFooter.style.opacity=this.phonePhaseProgress>=1?'1':'0';
    if (this.phonePhaseProgress < 1 || !entry || !this.config.showCardText) {this.mobileCaption.style.opacity='0'; return;}
    const data=this.cards[entry.i];
    if (this.captionIndex!==entry.i) {
      this.captionIndex=entry.i;
      this.mobileCaptionTitle.textContent=data.title||'';
      this.mobileCaptionLabel.textContent=data.label||this.config.mobileStoryEyebrow||'';
      this.mobileCaptionDescription.textContent=data.description||'';
    }
    const width=Math.min(rr.w*.9,Math.max(rr.w*.64,entry.p.width));
    this.mobileCaption.style.width=`${width}px`;
    const height=this.mobileCaption.offsetHeight;
    const y=entry.p.top - Math.max(10, Number(this.config.mobileCopyGap)||14) - height;
    const x=this.clamp((entry.p.left+entry.p.right)/2, width/2+8, rr.w-width/2-8);
    this.mobileCaption.style.transform=`translate3d(${x-width/2}px,${Math.max(14,y)}px,0)`;
    const alpha=this.ease(this.range(y,8,30))*(1-this.ease(this.range(entry.p.width/rr.w,1.04,1.28)));
    this.mobileCaption.style.opacity=String(alpha);
  }

  stopMobileMotion() {
    for (const item of this.animatedCards||[]) {
      item.active=false; item.fade=0;
      if (item.domWrapper) {item.domWrapper.style.opacity='0';item.domWrapper.style.visibility='hidden';}
      if (item.video) item.video.pause();
      if (item.domImage && !item.loading) {item.domImage.removeAttribute('src');item.domSrc='';item.loaded=false;}
    }
  }

  syncAnimatedMedia(time,dt) {
    if (!this.coversReady || !this.renderRect) return;
    const rr=this.renderRect;
    const maximum=Math.max(1,Math.min(2,Number(this.config.maxAnimatedMobile)||1));
    const canPlay=this.allowAnimatedWebp && this.visible && !document.hidden && !this.reduceMotionPreferred;
    const candidates=canPlay ? this.animatedCards.map(item=>({item,p:this.projectCardSurface(item.group)}))
      .filter(e=>e.p && !e.item.loadFailed && e.p.forward>3.0 && e.p.forward<(Number(this.config.animationActivationDistance)||20)
        && e.p.width>rr.w*.30 && e.p.width<rr.w*1.05 && e.p.height<rr.h*.9
        && e.p.right>0 && e.p.left<rr.w && e.p.bottom>0 && e.p.top<rr.h)
      .sort((a,b)=>a.p.forward-b.p.forward):[];
    const chosen=[];
    for (const e of candidates) {
      if (chosen.length>=maximum) break;
      // DOM images cannot depth-test: never place a farther animation over a nearer GLB card.
      const occluded=this.cardGroups.some(g=>{
        if(g===e.item.group) return false;
        const p=this.projectCardSurface(g,1,1);
        return p && p.forward>0 && p.forward<e.p.forward-.1 && this.rectOverlapRatio(p,e.p)>.03;
      });
      if(!occluded && !chosen.some(c=>this.rectOverlapRatio(c.p,e.p)>.03)) chosen.push(e);
    }
    const active=new Set(chosen.map(e=>e.item));
    if (this.phonePhaseProgress >= .8) {
      const index = chosen[0]?.item.index ?? Math.min(this.cards.length - 1,
        Math.floor((this.tunnelPhaseProgress || 0) * this.cards.length));
      this.prefetchNextMobileWebp(chosen.length ? index + 1 : index);
    }
    for(const item of this.animatedCards) {
      item.active=active.has(item);
      item.fade=THREE.MathUtils.damp(item.fade||0,item.active&&item.loaded?1:0,16,dt);
      if(item.active) {
        item.lastActiveAt=time;
        if(!item.loaded && !item.loading && !item.loadFailed) {
          // One active + one outgoing source at most; never start a third decoder.
          const assigned=this.animatedCards.filter(a=>a.domSrc || a.loading || (a.video&&!a.video.paused)).length;
          if(assigned<2) this.ensureAnimatedLoaded(item).then(()=>{if(!this.dead&&!item.loaded)item.loadFailed=true;});
        }
      } else if(time-(item.lastActiveAt||0)>240 && item.fade<.02 && !item.loading) {
        if(item.domImage && item.domSrc) {item.domImage.removeAttribute('src');item.domSrc='';item.loaded=false;}
      }
      if(item.video) {
        item.surface.material.opacity=item.fade;
        item.surface.visible=item.loaded&&item.fade>.01;
        if(item.active&&item.video.paused)item.video.play()?.catch(()=>{});
        if(!item.active&&!item.video.paused)item.video.pause();
      }
    }
  }

  updateWebpOverlays() {
    const rr=this.renderRect;
    if(!rr) return;
    for(const item of this.animatedCards) {
      if(!item.domWrapper) continue;
      const node=item.domWrapper, img=item.domImage;
      const p=item.loaded&&item.fade>.01&&this.allowAnimatedWebp?this.projectCardSurface(item.group):null;
      if(!p || p.forward<2.8 || p.width>rr.w*1.08 || p.height>rr.h*.95 || p.right<0 || p.left>rr.w) {
        node.style.visibility='hidden';node.style.opacity='0';continue;
      }
      const matrix=this.homographyMatrix(p.points,640,640/item.aspect);
      if(!matrix || /NaN|Infinity/.test(matrix)) {node.style.visibility='hidden';continue;}
      node.style.transform=matrix;
      node.style.opacity=String(item.fade);
      node.style.visibility='visible';
      node.style.zIndex=String(1000-Math.round(p.forward*10));
      img.style.opacity='1';img.style.visibility='visible';
    }
  }

  tick(time) {
    if(this.dead) return;
    this.frame=requestAnimationFrame(t=>this.tick(t));
    if(!this.visible || document.hidden || !this.camera) return;
    // Phone geometry and WebGL scissor are updated together, on the same frame.
    // No CSS transition runs separately from the 3D camera.
    const moving=this.phonePhaseProgress<1;
    if(!moving && time-this.lastSceneRenderAt<31) return;
    this.lastSceneRenderAt=time;
    const dt=Math.min((time-this.last)/1000,.05);this.last=time;
    this.tunnelProgress=this.reduceMotionPreferred?this.tunnelTarget:THREE.MathUtils.damp(this.tunnelProgress,this.tunnelTarget,12,dt);
    const spacing=Number(this.config.cardSpacingMobile)||12;
    const travel=(this.cards.length-1)*spacing+3.2;
    this.camera.position.set(0,3.1,-2.2-this.tunnelProgress*travel);
    this.camera.lookAt(0,3.15,this.camera.position.z-15);
    for(const g of this.cardGroups) {
      const d=this.camera.position.z-g.position.z;
      const far=THREE.MathUtils.smoothstep(d,11,spacing+12);
      const pass=1-THREE.MathUtils.smoothstep(d,1.0,4.6);
      g.position.x=g.userData.side*(1.15*far+4.0*pass);
      g.position.y=this.lerp(2.5,4.0,far);
      g.visible=d>-.5;
    }
    if(!this.reduceMotionPreferred)for(const a of this.anim)a(time*.001,dt);
    const W=this.lastW||this.sticky.clientWidth,H=this.lastH||this.sticky.clientHeight;
    const rr=this.renderRect||{x:0,y:0,w:W,h:H,radius:0};
    this.renderer.setScissorTest(false);this.renderer.setClearColor(0,0);this.renderer.clear(true,true,true);
    if(rr.w<2||rr.h<2)return;
    this.renderer.setViewport(rr.x,H-rr.y-rr.h,rr.w,rr.h);
    this.renderer.setScissor(rr.x,H-rr.y-rr.h,rr.w,rr.h);
    this.renderer.setScissorTest(true);
    this.camera.aspect=rr.w/rr.h;
    const hfov=(Number(this.config.mobileHorizontalFov)||38)*Math.PI/180;
    this.camera.fov=2*Math.atan(Math.tan(hfov/2)/this.camera.aspect)*180/Math.PI;
    this.camera.updateProjectionMatrix();this.camera.updateMatrixWorld(true);
    this.scene.updateMatrixWorld(true);
    this.updateCardCopies();
    this.syncAnimatedMedia(time,dt);
    this.updateWebpOverlays();
    this.renderer.render(this.scene,this.camera);this.renderer.setScissorTest(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.querySelectorAll('.vpt-mobile-motion').forEach(el=>el.remove());
    this.mobileTrack?.style.removeProperty('height');
    ['width','left','top'].forEach(k=>this.phone?.style.removeProperty(k));
    this.host.style.removeProperty('--vpt-mobile-top');
    this.cardCopyEls?.forEach(el=>el.removeAttribute('style'));
    if(this.mobileCaption) this.mobileCaption.style.opacity='0';
    if(this.mobileFooter) this.mobileFooter.style.opacity='0';
    delete this.host.dataset.mobileEngine;
  }
}

/* Both breakpoints now use real Three.js + GLB. No 2D gallery branch. */
class VogalabPhoneTunnel extends HTMLElement {
  connectedCallback() {
    if (this._routerActive) return;
    this._routerActive = true;
    this._mq = matchMedia('(max-width: 749px)');
    this._change = () => this._mount();
    this._mq.addEventListener('change', this._change);
    this._mount();
  }
  async _mount() {
    const serial = this._serial = (this._serial || 0) + 1;
    this._stopEngine();
    this.dataset.ready = 'false';
    this.dataset.vptBuild = 'mobile-glb-restored-5.1';
    const mobile = this._mq.matches;
    this.dataset.layout = mobile ? 'mobile' : 'desktop';
    this.querySelector('.vpt-scroll-space')?.setAttribute('aria-hidden', 'false');
    try {
      await loadDesktopLibraries();
      if (!this.isConnected || serial !== this._serial) return;
      this._engine = mobile ? new MobilePhoneTunnel(this) : new DesktopPhoneTunnel(this);
      this._engine.connectedCallback();
    } catch (error) {
      console.error('[VOGALAB 3D]', error);
      const status = this.querySelector('[data-vpt-status]');
      if (status) { status.style.display = 'block'; status.textContent = '3D 加载未完成：' + (error.message || String(error)); }
      this.dataset.loadError = 'libraries';
    }
  }
  _stopEngine() { this._engine?.disconnectedCallback(); this._engine = null; }
  disconnectedCallback() {
    this._serial = (this._serial || 0) + 1;
    this._mq?.removeEventListener('change', this._change);
    this._stopEngine();
    this._routerActive = false;
  }
}
if (!customElements.get('vogalab-phone-tunnel')) customElements.define('vogalab-phone-tunnel', VogalabPhoneTunnel);
